import { pieceApi } from '@api/piece';
import { ThunkDispatch } from '@reduxjs/toolkit';

import { apiRequest } from '@src/api/apiRequest';
import { uploadImageToS3 } from '@src/api/imageS3';
import { resizeImage } from '@utils/image';

import { getUploadPiecePageUrl } from '@src/api/piece';

import { PiecePageInitiationData, PiecePageInitiationImages } from './types';

interface Uris {
  originalKey: string;
  croppedKey: string;
  thumbnailKey: string;
}

const convertAndUploadImagesToS3 = async (image: string, cropped: string, pieceId: number) => {
  const contentType = 'image/jpg';
  const retriesPerImage = 5;

  const uris = await getUploadPiecePageUrl(pieceId);
  const thumbnailUploadCondition = !!cropped || !!image || false;

  const originalResizedImage = image ? await resizeImage(image, 4096, 4096) : '';
  const croppedResizedImage = cropped ? await resizeImage(cropped, 4096, 4096) : '';
  const thumbnailResizedImage = thumbnailUploadCondition ? await resizeImage(cropped || image || '', 300, 300) : '';

  const promises = [];

  try {
    if (originalResizedImage) {
      promises.push(
        apiRequest(
          uploadImageToS3,
          { image: originalResizedImage, uploadUrl: uris.originalUrl, contentType },
          false,
          retriesPerImage,
        ),
      );
    }

    if (croppedResizedImage) {
      promises.push(
        apiRequest(
          uploadImageToS3,
          { image: croppedResizedImage, uploadUrl: uris.croppedUrl, contentType },
          false,
          retriesPerImage,
        ),
      );
    }

    if (thumbnailResizedImage) {
      promises.push(
        apiRequest(
          uploadImageToS3,
          { image: thumbnailResizedImage, uploadUrl: uris.thumbnailUrl, contentType },
          false,
          retriesPerImage,
        ),
      );
    }
  } catch (error) {
    console.error(error);
    throw error;
  }

  await Promise.all(promises);

  return {
    uris: {
      originalKey: image ? uris.originalKey : '',
      croppedKey: cropped ? uris.croppedKey : '',
      thumbnailKey: thumbnailUploadCondition ? uris.thumbnailKey : '',
    },
    thumbnailImage: thumbnailResizedImage,
  };
};

const createPiecePage = async (
  data: PiecePageInitiationData,
  uris: Uris,
  croppedImage: string,
  dispatch: ThunkDispatch<any, any, any>,
) => {
  try {
    const { pieceDoc, thumbnail, pieceId } = data;

    const page = await dispatch(
      pieceApi.endpoints.createPiecePage.initiate({
        pieceDocId: pieceDoc.id,
        originalKey: uris.originalKey,
        croppedKey: croppedImage ? uris.croppedKey : undefined,
        thumbnailKey: uris.thumbnailKey,
        sortOrder: thumbnail.order,
        pieceId,
        skipInvalidation: true,
      }),
    ).unwrap();

    return {
      id: page.id,
      image: page.originalUrl,
      croppedImage: page.croppedUrl,
      thumbnailImage: page.thumbnailUrl,
      order: page.sortOrder,
    };
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const updatePiecePage = async (data: PiecePageInitiationData, uris: Uris, dispatch: ThunkDispatch<any, any, any>) => {
  try {
    const { pieceDoc, thumbnail, pieceId } = data;
    const currentPage = pieceDoc.piecePages.find((page) => page.id === thumbnail.id);

    const page = await dispatch(
      pieceApi.endpoints.updatePiecePage.initiate({
        id: thumbnail.id as number,
        pieceDocId: pieceDoc.id,
        originalKey: uris.originalKey || currentPage!.originalKey,
        croppedKey: uris.croppedKey || currentPage!.croppedKey,
        thumbnailKey: uris.thumbnailKey || currentPage!.thumbnailKey,
        sortOrder: currentPage!.sortOrder,
        pieceId,
      }),
    ).unwrap();

    return {
      id: page.id,
      image: page.originalUrl,
      croppedImage: page.croppedUrl,
      thumbnailImage: page.thumbnailUrl,
      order: page.sortOrder,
    };
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const runPiecePageInitiationQueries = async (
  data: PiecePageInitiationData,
  images: PiecePageInitiationImages,
  dispatch: ThunkDispatch<any, any, any>,
) => {
  const { thumbnail } = data;
  const { originalImage, croppedImage } = images;

  try {
    const { uris } = await convertAndUploadImagesToS3(originalImage, croppedImage, data.pieceId);
    return typeof thumbnail.id === 'string'
      ? await createPiecePage(data, uris, croppedImage, dispatch)
      : await updatePiecePage(data, uris, dispatch);
  } catch (error) {
    console.error(error);
  }
};
